// src/pages/MembersPage.js
//
// Ticket 17: org admin member management. Two tabs:
//   - Members: current memberships for the active org. Org admins can
//     change role (orgAdmin <-> reviewer) or remove a member.
//   - Invites: pending invites for the active org. Admins can revoke
//     them or create new ones via a simple form.
//
// The signed-in admin cannot demote or remove themselves — that path
// is blocked client-side so a single admin doesn't lock themselves out
// of their own org.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Users,
  Mail,
  Trash2,
  Shield,
  UserPlus,
  AlertTriangle,
  CheckCircle,
  Loader,
  X,
} from 'lucide-react';
import { useSession } from '../auth/sessionContext';
import {
  listMembershipsForOrg,
  updateMembershipRole,
  removeMembership,
} from '../auth/membershipHelpers';
import {
  listPendingInvitesForOrg,
  createInvite,
  revokeInvite,
} from '../data/invitesHelpers';

const TABS = [
  { key: 'members', label: 'Members' },
  { key: 'invites', label: 'Pending invites' },
];

const MembersPage = () => {
  const { activeOrg, user } = useSession();
  const orgId = activeOrg?.orgId || null;

  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  // invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('reviewer');

  const load = useCallback(async () => {
    if (!orgId) return;
    setFetching(true);
    try {
      const [m, i] = await Promise.all([
        listMembershipsForOrg(orgId),
        listPendingInvitesForOrg(orgId),
      ]);
      setMembers(m);
      setInvites(i);
    } catch (err) {
      setStatus({ type: 'error', message: `Load failed: ${err.message}` });
    } finally {
      setFetching(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRoleChange = async (membership, nextRole) => {
    if (membership.role === nextRole) return;
    if (membership.uid === user?.uid) {
      setStatus({ type: 'error', message: 'You cannot change your own role.' });
      return;
    }
    setStatus({ type: 'loading', message: 'Updating role...' });
    try {
      await updateMembershipRole(membership.id, nextRole);
      setStatus({ type: 'success', message: `Role changed to ${nextRole}.` });
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Role change failed: ${err.message}` });
    }
  };

  const handleRemove = async (membership) => {
    if (membership.uid === user?.uid) {
      setStatus({ type: 'error', message: 'You cannot remove yourself.' });
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${membership.email || membership.uid}?`)) return;
    setStatus({ type: 'loading', message: 'Removing member...' });
    try {
      await removeMembership(membership.id);
      setStatus({ type: 'success', message: 'Member removed.' });
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Remove failed: ${err.message}` });
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      setStatus({ type: 'error', message: 'Enter an email.' });
      return;
    }
    setStatus({ type: 'loading', message: 'Creating invite...' });
    try {
      await createInvite({
        orgId,
        email,
        role: inviteRole,
        invitedBy: user.uid,
      });
      setInviteEmail('');
      setStatus({
        type: 'success',
        message:
          `Invite created for ${email}. No email is sent automatically yet; ask them to sign in with this exact address to accept it.`,
      });
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Invite failed: ${err.message}` });
    }
  };

  const handleRevokeInvite = async (invite) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Revoke invite for ${invite.email}?`)) return;
    setStatus({ type: 'loading', message: 'Revoking invite...' });
    try {
      await revokeInvite(invite.id);
      setStatus({ type: 'success', message: 'Invite revoked.' });
      await load();
    } catch (err) {
      setStatus({ type: 'error', message: `Revoke failed: ${err.message}` });
    }
  };

  const statusStyles = {
    idle: 'bg-slate-100 text-slate-700',
    info: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800',
    loading: 'bg-yellow-100 text-yellow-800',
  };

  if (!activeOrg) {
    return (
      <main className="p-8">
        <div className="text-slate-500">No active organization.</div>
      </main>
    );
  }

  return (
    <main className="p-4 lg:p-8 pb-32">
      <div className="bg-white p-6 rounded-xl border border-slate-200 academic-shadow-lg max-w-5xl">
        <h2 className="text-2xl font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Users size={22} /> Member management
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          Manage members and pending invites for <strong>{activeOrg.orgName}</strong>.
        </p>

        {status.message && (
          <div
            className={`p-3 rounded-lg mb-4 text-sm flex items-center gap-3 ${statusStyles[status.type]}`}
          >
            {status.type === 'error' && <AlertTriangle size={16} />}
            {status.type === 'success' && <CheckCircle size={16} />}
            {status.type === 'loading' && <Loader size={16} className="animate-spin" />}
            <span>{status.message}</span>
            <button
              onClick={() => setStatus({ type: 'idle', message: '' })}
              className="ml-auto text-xs underline opacity-70"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <div className="flex gap-1 border-b border-slate-200 mb-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {t.label}
              {t.key === 'invites' && invites.length > 0 && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 rounded-full px-2 py-0.5">
                  {invites.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {fetching ? (
          <div className="py-8 text-slate-500 flex items-center gap-3">
            <Loader size={20} className="animate-spin" /> Loading {tab}...
          </div>
        ) : tab === 'members' ? (
          <MembersTable
            members={members}
            currentUid={user?.uid}
            onRoleChange={handleRoleChange}
            onRemove={handleRemove}
          />
        ) : (
          <InvitesTab
            invites={invites}
            onRevoke={handleRevokeInvite}
            onCreate={handleCreateInvite}
            inviteEmail={inviteEmail}
            setInviteEmail={setInviteEmail}
            inviteRole={inviteRole}
            setInviteRole={setInviteRole}
          />
        )}
      </div>
    </main>
  );
};

const MembersTable = ({ members, currentUid, onRoleChange, onRemove }) => {
  if (members.length === 0) {
    return <div className="py-8 text-slate-500 italic">No members yet.</div>;
  }
  return (
    <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
      {members.map((m) => {
        const self = m.uid === currentUid;
        return (
          <li key={m.id} className="p-4 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">
                {m.displayName || m.email || m.uid}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {m.email} {self && <span className="text-blue-600 ml-1">(you)</span>}
              </div>
            </div>
            <select
              value={m.role}
              onChange={(e) => onRoleChange(m, e.target.value)}
              disabled={self}
              className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus-academic disabled:bg-slate-100 disabled:text-slate-400"
              aria-label={`Change role for ${m.email}`}
            >
              <option value="orgAdmin">orgAdmin</option>
              <option value="reviewer">reviewer</option>
            </select>
            <button
              onClick={() => onRemove(m)}
              disabled={self}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:text-slate-300 disabled:hover:bg-transparent"
              aria-label={`Remove ${m.email}`}
            >
              <Trash2 size={16} />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const InvitesTab = ({
  invites,
  onRevoke,
  onCreate,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
}) => (
  <>
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
      Creating an invite adds a pending access record inside the platform. It does not
      send an email yet. Share the login URL out-of-band and ask the reviewer to sign in
      with the exact invited email address.
    </div>

    <form
      onSubmit={onCreate}
      className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg flex flex-col sm:flex-row gap-3"
    >
      <input
        type="email"
        placeholder="invitee@institution.edu"
        value={inviteEmail}
        onChange={(e) => setInviteEmail(e.target.value)}
        required
        className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus-academic"
      />
      <select
        value={inviteRole}
        onChange={(e) => setInviteRole(e.target.value)}
        className="px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 focus-academic"
      >
        <option value="reviewer">reviewer</option>
        <option value="orgAdmin">orgAdmin</option>
      </select>
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center gap-2"
      >
        <UserPlus size={16} /> Invite
      </button>
    </form>

    {invites.length === 0 ? (
      <div className="py-8 text-slate-500 italic">No pending invites.</div>
    ) : (
      <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
        {invites.map((i) => (
          <li key={i.id} className="p-4 flex items-center gap-3">
            <Mail size={18} className="text-blue-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">
                {i.email}
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Shield size={12} /> {i.role}
              </div>
            </div>
            <button
              onClick={() => onRevoke(i)}
              className="px-3 py-1 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg text-xs font-semibold flex items-center gap-1"
            >
              <X size={14} /> Revoke
            </button>
          </li>
        ))}
      </ul>
    )}
  </>
);

export default MembersPage;
